import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X } from 'lucide-react';
import { useBudget } from '../../context/BudgetContext';
import { todayStr, yesterdayStr, formatCurrency } from '../../utils/formatting';
import { getCurrencyInfo } from '../../utils/currencies';
import { submitTransaction as runSubmit } from '../../utils/submitTransaction';
import { CategorySelect, SearchableSelect } from '../Common/Common';

const TYPES = [
  { id: 'income',  label: 'Income',  cls: 'income' },
  { id: 'expense', label: 'Expense', cls: 'expense' },
  { id: 'savings', label: 'Savings', cls: 'savings' },
];

export default function MobileEntrySheet({ open, onClose, editTxn = null }) {
  const {
    settings, categories, buckets, yearlyExpenses,
    currencyBalances, addTransaction, updateTransaction,
    deductFromCurrencyBalance, addYearlyExpense, showToast,
  } = useBudget();

  const mainCurrency = settings.currency || 'ILS';

  const empty = {
    date: todayStr(), type: 'expense', category: '', description: '',
    amount: '', bucket: '', currency: mainCurrency, exchangeRate: '', fromBalance: false, tags: '',
  };
  const [form, setForm] = useState(empty);
  // Symbol reflects the currently selected currency, not just the main one
  const symbol = getCurrencyInfo(form.currency)?.symbol || form.currency || mainCurrency;
  const [errors, setErrors] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const amountRef = useRef();
  const sheetRef = useRef();

  const isEditing = !!editTxn;

  // Build a form from an existing transaction (mirrors Tracker.startEdit)
  function formFromTxn(t) {
    const hasForeign = t.currency && t.currency !== mainCurrency && Number.isFinite(t.foreignAmount);
    return {
      date: t.date, type: t.type, category: t.category || '', description: t.description,
      amount: hasForeign ? String(t.foreignAmount) : String(t.amount),
      bucket: t.bucket || '',
      currency: t.currency || mainCurrency,
      exchangeRate: hasForeign && Number.isFinite(t.exchangeRate) ? String(t.exchangeRate) : '',
      fromBalance: !!t.currencyBalanceId || !!t.fromBalance,
      tags: (t.tags || []).join(', '),
    };
  }

  // Reset/prefill on open + autofocus amount
  useEffect(() => {
    if (open) {
      if (editTxn) {
        const f = formFromTxn(editTxn);
        setForm(f);
        setShowAdvanced(f.currency !== mainCurrency);
      } else {
        setForm({ ...empty, date: todayStr() });
        setShowAdvanced(false);
      }
      setErrors({});
      setTimeout(() => amountRef.current?.focus(), 120);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editTxn]);

  // Esc closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const setF = (k, v) => { setForm(p => ({ ...p, [k]: v })); setErrors(e => ({ ...e, [k]: undefined })); };

  // Top-6 categories for the active type, by usage (most-tapped tiles)
  const topCategories = useMemo(() => {
    const relevant = categories.filter(c => c.type === (form.type === 'income' ? 'income' : 'expense'));
    if (form.type === 'savings') return [];
    return relevant.slice(0, 6);
  }, [categories, form.type]);

  function handleSubmit() {
    const ok = runSubmit(form, {
      settings, currencyBalances, categories,
      addTransaction, updateTransaction, deductFromCurrencyBalance,
      showToast, setErrors, editId: editTxn ? editTxn.id : null,
    });
    if (ok) onClose();
  }

  if (!open) return null;

  const validAmount = parseFloat(form.amount) > 0;
  const validCategory = form.type === 'savings' ? !!form.bucket : !!form.category;

  return (
    <div
      className="modal-backdrop modal-backdrop-sheet"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal modal-sheet" ref={sheetRef}>
        <div className="modal-sheet-grabber" aria-hidden="true" />

        <div className="modal-header-row">
          <h3 className="modal-title" style={{ margin: 0 }}>{isEditing ? 'Edit transaction' : 'Add transaction'}</h3>
          <button className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Close">
            <X size={14} />
          </button>
        </div>

        <div className="modal-sheet-body">
          {/* Type segmented control */}
          <div className="entry-type-segmented" role="tablist">
            {TYPES.map(t => (
              <button
                key={t.id}
                role="tab"
                aria-selected={form.type === t.id}
                className={`entry-type-pill ${t.cls}${form.type === t.id ? ' active' : ''}`}
                onClick={() => { setF('type', t.id); setF('category', ''); setF('bucket', ''); }}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Big amount field */}
          <div className="entry-amount-wrap">
            <span className="entry-amount-symbol">{symbol}</span>
            <input
              ref={amountRef}
              className={`entry-amount-input${errors.amount ? ' input-error' : ''}`}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder="0.00"
              value={form.amount}
              onChange={(e) => setF('amount', e.target.value.replace(/[^\d.,]/g, '').replace(',', '.'))}
            />
          </div>
          {errors.amount && <div className="error-msg" style={{ textAlign: 'center' }}>{errors.amount}</div>}

          {/* Category grid (expense/income) or bucket select (savings) */}
          {form.type !== 'savings' ? (
            <>
              <div className="entry-section-label">Category</div>
              <div className="entry-category-grid">
                {topCategories.map(c => (
                  <button
                    key={c.id}
                    className={`entry-cat-tile${form.category === c.id ? ' active' : ''}`}
                    onClick={() => setF('category', c.id)}
                  >
                    <span className="entry-cat-name">{c.name}</span>
                  </button>
                ))}
              </div>
              <div className="entry-cat-more">
                <CategorySelect
                  value={form.category}
                  onChange={id => setF('category', id)}
                  categories={categories}
                  yearlyExpenses={yearlyExpenses}
                  type={form.type === 'income' ? 'income' : 'expense'}
                  addYearlyExpense={form.type === 'expense' ? addYearlyExpense : undefined}
                  showToast={showToast}
                  className={errors.category ? 'input-error' : ''}
                  placeholder="More categories…"
                />
              </div>
              {errors.category && <div className="error-msg">{errors.category}</div>}
            </>
          ) : (
            <>
              <div className="entry-section-label">Savings bucket</div>
              <SearchableSelect
                value={form.bucket}
                onChange={id => setF('bucket', id)}
                options={buckets.map(b => ({ value: b.id, label: b.name }))}
                placeholder="Select bucket…"
                className={errors.bucket ? 'input-error' : ''}
              />
              {errors.bucket && <div className="error-msg">{errors.bucket}</div>}
            </>
          )}

          {/* Description */}
          <div className="entry-section-label">Description</div>
          <input
            type="text"
            placeholder="What did you buy/earn?"
            value={form.description}
            onChange={(e) => setF('description', e.target.value)}
            maxLength={100}
            className={errors.description ? 'input-error' : ''}
            style={{ width: '100%' }}
            autoComplete="off"
          />
          {errors.description && <div className="error-msg">{errors.description}</div>}

          {/* Date chips */}
          <div className="entry-section-label">Date</div>
          <div className="entry-date-chips">
            <button
              className={`entry-chip${form.date === todayStr() ? ' active' : ''}`}
              onClick={() => setF('date', todayStr())}
            >Today</button>
            <button
              className={`entry-chip${form.date === yesterdayStr() ? ' active' : ''}`}
              onClick={() => setF('date', yesterdayStr())}
            >Yesterday</button>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setF('date', e.target.value)}
              className="entry-date-input"
            />
          </div>

          {/* Advanced: foreign currency */}
          <button
            className="entry-advanced-toggle"
            onClick={() => setShowAdvanced(s => !s)}
          >
            {showAdvanced ? 'Hide foreign currency' : 'Add foreign currency'}
          </button>

          {showAdvanced && (
            <div className="entry-advanced">
              <div className="entry-section-label">Currency</div>
              <SearchableSelect
                value={form.currency}
                onChange={code => { setF('currency', code); setF('fromBalance', false); setF('exchangeRate', ''); }}
                options={[mainCurrency, 'USD', 'EUR', 'GBP', 'CHF', 'JPY', 'CAD', 'AUD']
                  .filter((c, i, a) => a.indexOf(c) === i)
                  .map(code => ({ value: code, label: code }))}
                placeholder="Currency…"
              />
              {form.currency !== mainCurrency && (
                <>
                  <div className="entry-section-label">
                    Rate ({mainCurrency}/{getCurrencyInfo(form.currency)?.symbol || form.currency})
                  </div>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="e.g. 3.65"
                    value={form.exchangeRate}
                    onChange={(e) => setF('exchangeRate', e.target.value.replace(/[^\d.,]/g, '').replace(',', '.'))}
                    style={{ width: '100%' }}
                  />
                  {parseFloat(form.exchangeRate) > 0 && parseFloat(form.amount) > 0 && (
                    <div className="muted" style={{ marginTop: 6, fontSize: 13 }}>
                      = {formatCurrency(parseFloat(form.amount) * parseFloat(form.exchangeRate), settings)}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="modal-sheet-footer">
          <button
            className="btn btn-primary entry-submit"
            disabled={!validAmount || !validCategory}
            onClick={handleSubmit}
          >
            {isEditing ? 'Save changes' : `Add ${TYPES.find(t => t.id === form.type)?.label.toLowerCase()}`}
          </button>
        </div>
      </div>
    </div>
  );
}
